import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Box } from "@mui/material";
import {
  getVideoSuiteType,
  SettingItemObjType,
  VideoAppType,
  videoModelType,
  VideoSettings,
  VideoSuiteAppInitStates,
  VideoSuiteInput,
  VideoSuiteSample,
  VideoSuiteType,
} from "../../../Types/VideoSuite-Types";
import SignalRService from "../../one-query/services/SignalRService";
import { MessageType, VideoSuiteApps } from "../../../Helper";
import {
  useGenerateRandomPrompt,
} from "../../../store/Apis/SuitesApps/ImageSuitesApi";
import VideoGeneratorApp from "./Apps/VideoGeneratorApp";
import ImageToVideoApp from "./Apps/ImageToVideoApp";
import { GetAvatarsRequestData, GetSoundEffectRequest, useGetAllSoundEffect, useGetAvatarsVoices, useGetVideoSuiteAppSettings, useImproveVideoSuitePrompt } from "../../../store/Apis/SuitesApps/VideoSuiteApi";
import { useGetUserCredits } from "../../../store/Apis/StripeApi";
import { useAppDispatch } from "../../../store/hooks";
import { setMessageErrorAppSuite } from "../../../store/account/userSlice";
import AvatarVideoApp from "./Apps/AvatarVideoApp";
import { useLocation } from "react-router-dom";
import { getBaseUrl } from "../../ImageSuite/Helper";

interface GenerationViewProps {
  activeItem: VideoAppType;
  VideoSuiteSamples: VideoSuiteSample[];
}
const VideoSuiteContainer = ({
  activeItem,
  VideoSuiteSamples,
}: GenerationViewProps) => {
  const { mutate: GenerateRandomPrompt, isPending: IsGeneratePromptLoading } =
    useGenerateRandomPrompt();
  const {
    mutate: ImproveVideoPromptRequest,
    isPending: isVideoPromptImproving,
  } = useImproveVideoSuitePrompt();
  const [isFinished, setIsFinished] = useState<boolean>(false);
  const [generatedPrompt, setGeneratedPrompt] = useState<string>("");
  const getUserCredits = useGetUserCredits();
  const [generateVideoSuites, setGenerateVideoSuites] = useState<{
    VideoGenerator: VideoSuiteInput;
    ImageToVideo: VideoSuiteInput;
    AvatarVideo: VideoSuiteInput;
  }>(VideoSuiteAppInitStates);
  const { data: videoSuiteAppSettings } = useGetVideoSuiteAppSettings(
    activeItem.id
    );
  const [soundEffectParams, setSoundEffectParams] = useState<GetSoundEffectRequest>({});
  const { data: soundEffectData ,isPending: isSoundEffectsLoading } = useGetAllSoundEffect(soundEffectParams);
  const dispatch = useAppDispatch();
  const location = useLocation();
  const hasRedirectApp = location?.state?.activeVideoAppData;

  useEffect(() => {
      if (activeItem?.id) {
        if (activeItem.type === VideoSuiteType.VideoGenerator) {
          setGenerateVideoSuites((prevState) => ({
            ...prevState,
            VideoGenerator: {
              ...prevState.VideoGenerator,
              settings: {
                ...prevState.VideoGenerator.settings,
                ...videoSuiteAppSettings,
                selectedModel: !generateVideoSuites.VideoGenerator.settings.selectedModel.version?{
                  name: videoSuiteAppSettings?.length > 0 ? videoSuiteAppSettings[0]?.name : "",
                  version: videoSuiteAppSettings?.length > 0 ? videoSuiteAppSettings[0]?.version : "",
                }: generateVideoSuites.VideoGenerator.settings.selectedModel
              },
            },
          }));
        } else if (activeItem.type === VideoSuiteType.ImageToVideo) {
          setGenerateVideoSuites((prevState) => ({
            ...prevState,
            ImageToVideo: {
              ...prevState.ImageToVideo,
              settings: {
                ...prevState.ImageToVideo.settings,
                ...videoSuiteAppSettings,
                selectedModel:!generateVideoSuites.ImageToVideo.settings.selectedModel.version? {
                  name:  videoSuiteAppSettings?.length > 0 ? videoSuiteAppSettings[0]?.name : "",
                  version: videoSuiteAppSettings?.length > 0 ? videoSuiteAppSettings[0]?.version : "",  
                }: generateVideoSuites.ImageToVideo.settings.selectedModel
              },
            },
          }));
        }else if (activeItem.type === VideoSuiteType.AvatarVideo) {
          setGenerateVideoSuites((prevState) => ({
            ...prevState,
            AvatarVideo: {
              ...prevState.AvatarVideo,
              settings: {
                ...prevState.AvatarVideo.settings,
                ...videoSuiteAppSettings,
                selectedModel: {
                  name: videoSuiteAppSettings?.length > 0 ? videoSuiteAppSettings[0]?.name : "",
                  version: videoSuiteAppSettings?.length > 0 ? videoSuiteAppSettings[0]?.version : "",  
                }
              },
            },
          }));
        }
      }
  }, [activeItem?.id, videoSuiteAppSettings]);
  
  const getActiveAppObj = useMemo(() => {
    switch (activeItem.type) {
      case VideoSuiteType.VideoGenerator:
        return generateVideoSuites.VideoGenerator;
      case VideoSuiteType.ImageToVideo:
        return generateVideoSuites.ImageToVideo;
      case VideoSuiteType.AvatarVideo:
        return generateVideoSuites.AvatarVideo;
      default:
        return null;
    }
  }, [activeItem, generateVideoSuites]);

  const getErrorMessage = (error: any) => {
    const responseData = error?.response?.data;
    if (responseData) {
      dispatch(setMessageErrorAppSuite(responseData));
    }
  };

  useEffect(() => {
    const selectedModel =
      activeItem.type === VideoSuiteType.VideoGenerator
        ? generateVideoSuites.VideoGenerator.settings.selectedModel.name
        : generateVideoSuites.ImageToVideo.settings.selectedModel.name;

    const modelData =
      videoSuiteAppSettings?.find(
        (model: VideoSettings) => model.name === selectedModel
      ) ||
      (videoSuiteAppSettings?.length && videoSuiteAppSettings[0]);

    const randomValue = Math.floor(Math.random() * 1000000 + 1);

    if (!modelData) return;
    const newSettings = {
      selectedModel: { name: modelData.name, version: modelData.version },
      selectedSize: modelData.modelDimensions?.[0]?.description || "",
      selectedStyle: modelData.modelStyles?.[0] || "",
      selectedDuration: modelData.modelDurations?.[0]?.value.toString() || "",
      selectedMode: modelData.modelVideoQualities?.[0]?.description || "",
      generateTime: modelData.generationTime,
      seed: randomValue,
      creditsNeeded:modelData?.creditsNeeded
    };
    if (activeItem.type === VideoSuiteType.VideoGenerator) {
      setGenerateVideoSuites((prevState) => ({
        ...prevState,
        VideoGenerator: {
          ...prevState.VideoGenerator,
          settings: {
            ...prevState.VideoGenerator.settings,
            ...newSettings,
          },
        },
      }));
    } else if (activeItem.type === VideoSuiteType.ImageToVideo) {
      setGenerateVideoSuites((prevState) => ({
        ...prevState,
        ImageToVideo: {
          ...prevState.ImageToVideo,
          settings: {
            ...prevState.ImageToVideo.settings,
            ...newSettings,
          },
        },
      }));
    } else if (activeItem.type === VideoSuiteType.AvatarVideo) {
      setGenerateVideoSuites((prevState) => ({
        ...prevState,
        AvatarVideo: {
          ...prevState.AvatarVideo,
          settings: {
            ...prevState.AvatarVideo.settings,
            ...newSettings,
          },
        },
      }));
    }
  }, [
    activeItem,
    generateVideoSuites.VideoGenerator.settings.selectedModel.name,
    generateVideoSuites.ImageToVideo.settings.selectedModel.name,
    generateVideoSuites.AvatarVideo.settings.selectedModel.name,
  ]);

  useEffect(() => {
    VideoSuiteSamples?.forEach((item: VideoSuiteSample) => {
      if (item.type === VideoSuiteType.VideoGenerator) {
        setGenerateVideoSuites((prevState) => ({
          ...prevState,
          VideoGenerator: {
            ...prevState.VideoGenerator,
            example: {
              ...item,
            },
          },
        }));
      } else if (item.type === VideoSuiteType.ImageToVideo) {
        setGenerateVideoSuites((prevState) => ({
          ...prevState,
          ImageToVideo: {
            ...prevState.ImageToVideo,
            example: {
              ...item,
            },
          },
        }));
      } else if (item.type === VideoSuiteType.AvatarVideo) {
        setGenerateVideoSuites((prevState) => ({
          ...prevState,
          AvatarVideo: {
            ...prevState.AvatarVideo,
            example: {
              ...item,
            },
          },
        }));
      }
    });
  }, [VideoSuiteSamples]);

  const onResultFinished = () => {
    getUserCredits();
  };

  useEffect(() => {
    if (isFinished) {
      setIsFinished(false);
      setGeneratedPrompt("");
      setGenerateVideoSuites((prevState) => {
        const newState = { ...prevState };
        switch (activeItem.type) {
          case VideoSuiteType.VideoGenerator:
            newState.VideoGenerator.isLoading = false;
            break;
          case VideoSuiteType.ImageToVideo:
            newState.ImageToVideo.isLoading = false;
            break;
          case VideoSuiteType.AvatarVideo:
            newState.AvatarVideo.isLoading = false;
            break;
        }
        return newState;
      });
      onResultFinished();
    }
  }, [isFinished]);

  const handleGenerateVideoSuitesSignalR = useCallback(
    (message: string, messageType: number) => {
      console.log("message messageType", messageType);
      console.log("message", message);

      if (message === "**/**Finished**/**") {
        setIsFinished(true);
      } else {
        if (messageType === MessageType.VideoSuitesGenerateVideo) {
          setGenerateVideoSuites((prevState) => ({
            ...prevState,
            VideoGenerator: {
              ...prevState.VideoGenerator,
              outputUrl: getBaseUrl(message),
              isLoading: true,
            },
          }));
        } else if (messageType === MessageType.VideoSuitesImageToVideo) {
          setGenerateVideoSuites((prevState) => ({
            ...prevState,
            ImageToVideo: {
              ...prevState.ImageToVideo,
              outputUrl: getBaseUrl(message),
              isLoading: true,
            },
          }));
        } else if (messageType === MessageType.VideoSuitesCustomAvatar) {
          setGenerateVideoSuites((prevState) => ({
            ...prevState,
            AvatarVideo: {
              ...prevState.AvatarVideo,
              outputUrl: message,
              isLoading: true,
            },
          }));
        }
      }
    },
    []
  );

  const setupSignalRConnection = useCallback(() => {
    const connection = SignalRService.getInstance().connection;
    connection.on("SendVideoMessage", handleGenerateVideoSuitesSignalR);
    return () => {
      connection.off("SendVideoMessage", handleGenerateVideoSuitesSignalR);
    };
  }, [handleGenerateVideoSuitesSignalR]);

  useEffect(() => {
    const cleanup = setupSignalRConnection();
    return () => {
      cleanup();
    };
  }, [setupSignalRConnection]);

  ///////////////random prompt//////////////////////
  const handelGenerateRandomPromptSignalR = useCallback(
    (
      modelName: string,
      modelVersion: string,
      message: string,
      tag: string,
      conversationId: string,
      messageId: string,
      messageType: number
    ) => {
      if (message === "**/**Finished**/**") {
        setIsFinished(true);
      } else {
        if (messageType === MessageType.Utility && message !== null) {
          setGeneratedPrompt((prev) => (prev ? prev + message : message));
        }
      }
    },
    []
  );

  const setupSignalRConnectionRandomPrompt = useCallback(() => {
    const connection = SignalRService.getInstance().connection;
    connection.on("SendModelMessage", handelGenerateRandomPromptSignalR);
    return () => {
      connection.off("SendModelMessage", handelGenerateRandomPromptSignalR);
    };
  }, [handelGenerateRandomPromptSignalR]);

  useEffect(() => {
    const cleanup = setupSignalRConnectionRandomPrompt();
    return () => {
      cleanup();
    };
  }, [setupSignalRConnectionRandomPrompt]);

  // ==============================================================

  // signalR to replace old video url with new AzureImage url, firts for performance BE save video locally,
  //  then to azure, after azure finish we remove drive video and replace it ith azure.
  const handleReplaceImageURL = useCallback(
    (
      messageId: string,
      responseId: string,
      oldImageUrl: string,
      newImageUrl: string,
      messageType: number
    ) => {
      
      if (messageType === MessageType.VideoSuitesGenerateVideo) {
        setGenerateVideoSuites((prevState) => ({
          ...prevState,
          VideoGenerator: {
            ...prevState.VideoGenerator,
            outputUrl: newImageUrl,
          },
        }));
      } else if (messageType === MessageType.VideoSuitesImageToVideo) {
        setGenerateVideoSuites((prevState) => ({
          ...prevState,
          ImageToVideo: {
            ...prevState.ImageToVideo,
            outputUrl: newImageUrl,
          },
        }));
      } else if (messageType === MessageType.VideoSuitesCustomAvatar) {
        setGenerateVideoSuites((prevState) => ({
          ...prevState,
          AvatarVideo: {
            ...prevState.AvatarVideo,
            outputUrl: newImageUrl,
          },
        }));
      }
    },
    []
  );

  const replaceURLImageSignalRConnection = useCallback(() => {
    const connection = SignalRService.getInstance().connection;
    connection.on("ReplaceOldImageUrl", handleReplaceImageURL);
    return () => {
      connection.off("ReplaceOldImageUrl", handleReplaceImageURL);
    };
  }, [handleGenerateVideoSuitesSignalR]);

  useEffect(() => {
    const cleanup = replaceURLImageSignalRConnection();
    return () => {
      cleanup();
    };
  }, [replaceURLImageSignalRConnection]);

  const handelGenerateRandomPrompt = async () => {
    setGeneratedPrompt("");
    const connectionId = await SignalRService.getInstance().getConnectionId();
    const RequestData = {
      connectionId: connectionId,
      type: getVideoSuiteType() + "",
    };
    if(RequestData.connectionId)
      GenerateRandomPrompt(RequestData);
  };

  const handleModelSettingChanges = (
    name: string,
    value: number | string | SettingItemObjType | videoModelType
  ) => {
    setGenerateVideoSuites((prevState) => {
      const newState = { ...prevState };
      switch (activeItem.type) {
        case VideoSuiteType.VideoGenerator:
          newState.VideoGenerator.settings = {
            ...newState.VideoGenerator.settings,
            [name]: value,
          };
          break;
        case VideoSuiteType.ImageToVideo:
          newState.ImageToVideo.settings = {
            ...newState.ImageToVideo.settings,
            [name]: value,
          };
          break;
        case VideoSuiteType.AvatarVideo:
          newState.AvatarVideo.settings = {
            ...newState.AvatarVideo.settings,
            [name]: value,
          };
          break;
      }
      return newState;
    });
  };

  const toggleLockSeed = (openLockSeed: boolean) => {
    setGenerateVideoSuites((prevState) => {
      const newState = { ...prevState };
      switch (activeItem.type) {
        case VideoSuiteType.VideoGenerator:
          newState.VideoGenerator.openLockSeed = openLockSeed;
          break;
        case VideoSuiteType.ImageToVideo:
          newState.ImageToVideo.openLockSeed = openLockSeed;
          break;
        case VideoSuiteType.AvatarVideo:
          newState.AvatarVideo.openLockSeed = openLockSeed;
          break;
      }
      return newState;
    });
  };
  const handleUpdateInputValue = (value: string) => {
    setGenerateVideoSuites((prevState) => {
      const newState = { ...prevState };
      switch (activeItem.type) {
        case VideoSuiteType.VideoGenerator:
          newState.VideoGenerator.inputValue = value;
          break;
        case VideoSuiteType.ImageToVideo:
          newState.ImageToVideo.inputValue = value;
          break;
        case VideoSuiteType.AvatarVideo:
          newState.AvatarVideo.inputValue = value;
          break;
      }
      return newState;
    });
  };
  const handleInputImageChange = (image: File | string | null) => {
    setGenerateVideoSuites((prevState) => {
      const newState = { ...prevState };
      switch (activeItem.type) {
        case VideoSuiteType.ImageToVideo:
          newState.ImageToVideo.inputImage = image;
          break;
        case VideoSuiteType.AvatarVideo:
          newState.AvatarVideo.inputImage = image;
          break;
      }
      return newState;
    });
  };

  const handleClearInput = () => {
    handleInputImageChange(null);
    setGenerateVideoSuites((prevState) => {
      const newState = { ...prevState };
      switch (activeItem.type) {
        case VideoSuiteType.ImageToVideo:
          newState.ImageToVideo.outputUrl = "";
          break;
        case VideoSuiteType.AvatarVideo:
          newState.AvatarVideo.outputUrl = "";
          break;
      }
      return newState;
    });
  };
  const handleSetIsLoading = (isLoading: boolean) => {
    setGenerateVideoSuites((prevState) => {
      const newState = { ...prevState };
      switch (activeItem.type) {
        case VideoSuiteType.VideoGenerator:
          newState.VideoGenerator.isLoading = isLoading;
          break;
        case VideoSuiteType.ImageToVideo:
          newState.ImageToVideo.isLoading = isLoading;
          break;
        case VideoSuiteType.AvatarVideo:
          newState.AvatarVideo.isLoading = isLoading;
          break;
      }
      return newState;
    });
  };
  const handleVideoWithSoundEffect = (newVideoUrl:string)=>{
  setGenerateVideoSuites((prevState) => {
      const newState = { ...prevState };
      switch (activeItem.type) {
        case VideoSuiteType.VideoGenerator:
          newState.VideoGenerator={
            ...newState.VideoGenerator,
            outputUrl: newVideoUrl
          }
          break;
        case VideoSuiteType.ImageToVideo:
          newState.ImageToVideo={
            ...newState.ImageToVideo,
            outputUrl: newVideoUrl
          }
          break;
      }
      return newState;
    })
    }
  const handelImprovePrompt = async () => {
    const connectionId = await SignalRService.getInstance().getConnectionId();
    if (getActiveAppObj?.inputValue) {
      const improvePromptRequestData = {
        prompt: getActiveAppObj?.inputValue,
        connectionId: connectionId,
      };
      ImproveVideoPromptRequest(improvePromptRequestData);
    }
  };

  // Handle video library data if available.
  useEffect(() => {
    if (hasRedirectApp) {
      if (hasRedirectApp.appType === VideoSuiteApps.VideoGenerator) {
        setGenerateVideoSuites((prevState) => ({
          ...prevState,
          VideoGenerator: {
            ...prevState.VideoGenerator,
            inputImage: hasRedirectApp?.input?.attachment || null,
            inputValue: hasRedirectApp?.input?.prompt || "",
            outputUrl: hasRedirectApp?.output || "",
          },
        }));
      }
    }
  }, [hasRedirectApp]);

  return (
    <Box className="generation-view">
      {getActiveAppObj?.type === VideoSuiteType.VideoGenerator ? (
      <VideoGeneratorApp
        videoSettings={videoSuiteAppSettings}
        activeAppProps={getActiveAppObj}
        generatedPrompt={generatedPrompt}
        setInputValue={handleUpdateInputValue}
        toggleLockSeed={toggleLockSeed}
        handleSetIsLoading={handleSetIsLoading}
        handleModelSettingChanges={handleModelSettingChanges}
        IsGeneratePromptLoading={IsGeneratePromptLoading}
        handelGenerateRandomPrompt={handelGenerateRandomPrompt}
        handleClearInput={handleClearInput}
        getErrorMessage={getErrorMessage}
        handelImprovePrompt={handelImprovePrompt}
        handleVideoWithSoundEffect={handleVideoWithSoundEffect}
        isVideoPromptImproving={isVideoPromptImproving}
        soundEffectData={soundEffectData|| []}
        setSoundEffectParams={setSoundEffectParams}
        isSoundEffectsLoading={isSoundEffectsLoading}
      />
      ) : getActiveAppObj?.type === VideoSuiteType.ImageToVideo ? (
      <ImageToVideoApp
        videoSettings={videoSuiteAppSettings}
        activeAppProps={getActiveAppObj}
        generatedPrompt={generatedPrompt}
        setInputValue={handleUpdateInputValue}
        handleSetIsLoading={handleSetIsLoading}
        toggleLockSeed={toggleLockSeed}
        handleInputImageChange={handleInputImageChange}
        handleModelSettingChanges={handleModelSettingChanges}
        IsGeneratePromptLoading={IsGeneratePromptLoading}
        handelGenerateRandomPrompt={handelGenerateRandomPrompt}
        handleClearInput={handleClearInput}
        getErrorMessage={getErrorMessage}
        handelImprovePrompt={handelImprovePrompt}
        handleVideoWithSoundEffect={handleVideoWithSoundEffect}
        isVideoPromptImproving={isVideoPromptImproving}
        soundEffectData={soundEffectData|| []}
        setSoundEffectParams={setSoundEffectParams}
        isSoundEffectsLoading={isSoundEffectsLoading}
      />
      ) : getActiveAppObj?.type === VideoSuiteType.AvatarVideo ? (
      <AvatarVideoApp
        videoSettings={videoSuiteAppSettings}
        activeAppProps={getActiveAppObj}
        generatedPrompt={generatedPrompt}
        setInputValue={handleUpdateInputValue}
        toggleLockSeed={toggleLockSeed}
        handleSetIsLoading={handleSetIsLoading}
        handleModelSettingChanges={handleModelSettingChanges}
        IsGeneratePromptLoading={IsGeneratePromptLoading}
        handelGenerateRandomPrompt={handelGenerateRandomPrompt}
        handleClearInput={handleClearInput}
        getErrorMessage={getErrorMessage}
        handelImprovePrompt={handelImprovePrompt}
        isVideoPromptImproving={isVideoPromptImproving}
      />
      ) : null}
    </Box>
  );
};

export default VideoSuiteContainer;
